import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  styled
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getAllUtilityServices } from '../../api/utilityServices';
import { UtilityService, UtilityType, Supplier } from '../../types/utility.types';
import * as supplierApi from '../../api/suppliers';
import * as lookupApi from '../../api/lookups';
import { LookupType, Lookup } from '../../types/lookup.types';

// Styled component for clickable row
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Contracts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contracts, setContracts] = useState<UtilityService[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [statusOptions, setStatusOptions] = useState<Lookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof UtilityService>('client');

  // Initialize filters from URL parameters
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const serviceTypeParam = searchParams.get('serviceType');
    const supplierParam = searchParams.get('supplier');

    if (statusParam) {
      setStatusFilter(statusParam);
    }
    if (serviceTypeParam) {
      setServiceTypeFilter(serviceTypeParam);
    }
    if (supplierParam) {
      setSupplierFilter(supplierParam);
    }
  }, [searchParams]);

  // Initial load with loading state
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchContracts();
      setLoading(false);
    };
    loadData();
  }, []);

  // Auto-refresh every 30 seconds (without loading state)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchContracts();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  const fetchContracts = async () => {
    try {
      const [fetchedContracts, fetchedSuppliers, fetchedStatuses] = await Promise.all([
        getAllUtilityServices(),
        supplierApi.fetchSuppliers(),
        lookupApi.getActiveLookupsByType('UtilityServiceStatus' as LookupType)
      ]);
      
      setContracts(fetchedContracts || []);
      setSuppliers(fetchedSuppliers || []);
      setStatusOptions(fetchedStatuses || []);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError('Failed to load contracts');
      toast.error('Failed to load contracts');
    }
  };
  
  // Helper function to get supplier name from ID or object
  const getSupplierName = (supplier: string | any): string => {
    // Handle populated supplier object
    if (typeof supplier === 'object' && supplier !== null && 'name' in supplier) {
      return supplier.name;
    }
    // Handle supplier ID - lookup in suppliers array
    const supplierId = typeof supplier === 'string' ? supplier : supplier?._id;
    const foundSupplier = suppliers.find(s => s._id === supplierId);
    return foundSupplier ? foundSupplier.name : 'Unknown Supplier';
  };

  const handleAddClick = () => {
    navigate('/utilities/contracts/new');
  };

  const handleContractClick = (contract: UtilityService) => {
    // Extract the property and client IDs properly
    const propertyId = typeof contract.property === 'object' ? contract.property._id : contract.property;
    const clientId = typeof contract.client === 'object' ? contract.client._id : contract.client;
    
    // Navigate to the contract details page with all required parameters
    navigate(`/utilities/contracts/${contract._id}?propertyId=${propertyId}&clientId=${clientId}`);
  };

  // Format service type in proper case for display
  const formatServiceType = (type: UtilityType | string): string => {
    if (!type) return '';
    
    // Handle special cases
    if (type === 'PPA Export Contract') return 'PPA Export Contract';
    if (type === 'EV') return 'EV';
    
    // Convert other types to proper case
    return type.toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusColor = (status: UtilityService['status']) => {
    // Default mapping if we don't have custom status options
    if (!statusOptions || statusOptions.length === 0) {
      switch (status) {
        case 'LIVE':
          return 'success';
        case 'PENDING':
          return 'warning';
        case 'TERMINATED':
          return 'error';
        default:
          return 'default';
      }
    }
    
    // Use the status from the AdminPanel's UtilityServiceStatus
    const statusOption = statusOptions.find(opt => opt.value === status);
    if (!statusOption) return 'default';
    
    // You could add a color field to the Lookup type and use it here
    // For now, we'll use the same mapping as before
    switch (status) {
      case 'LIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'TERMINATED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filtered and sorted contracts
  const filteredContracts = useMemo(() => {
    let filtered = contracts.filter(contract => {
      const matchesSearch = !searchTerm || 
        (contract.client?.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         contract.property?.address?.line1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         contract.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
      
      // Handle supplier filter - supplier can be string ID or populated object
      const supplierId = typeof contract.supplier === 'string' 
        ? contract.supplier 
        : contract.supplier?._id;
      const matchesSupplier = supplierFilter === 'all' || supplierId === supplierFilter;
      
      const matchesServiceType = serviceTypeFilter === 'all' || contract.serviceType === serviceTypeFilter;
      
      return matchesSearch && matchesStatus && matchesSupplier && matchesServiceType;
    });

    // Apply sorting
    if (orderBy) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (orderBy) {
          case 'client':
            aValue = a.client?.companyName || '';
            bValue = b.client?.companyName || '';
            break;
          case 'property':
            aValue = a.property?.address?.line1 || '';
            bValue = b.property?.address?.line1 || '';
            break;
          case 'accountNumber':
            aValue = a.accountNumber || '';
            bValue = b.accountNumber || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'supplier':
            aValue = a.supplier || '';
            bValue = b.supplier || '';
            break;
          case 'serviceType':
            aValue = a.serviceType || '';
            bValue = b.serviceType || '';
            break;
          case 'contractStartDate':
            aValue = a.contractStartDate ? new Date(a.contractStartDate).getTime() : 0;
            bValue = b.contractStartDate ? new Date(b.contractStartDate).getTime() : 0;
            break;
          case 'contractEndDate':
            aValue = a.contractEndDate ? new Date(a.contractEndDate).getTime() : 0;
            bValue = b.contractEndDate ? new Date(b.contractEndDate).getTime() : 0;
            break;
          default:
            return 0;
        }
        
        if (typeof aValue === 'number') {
          return order === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      });
    }

    return filtered;
  }, [contracts, searchTerm, statusFilter, supplierFilter, serviceTypeFilter, order, orderBy]);

  const paginatedContracts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredContracts.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredContracts, page, rowsPerPage]);

  const uniqueServiceTypes = useMemo(() => {
    const types = new Set(contracts.map(c => c.serviceType).filter(Boolean));
    return Array.from(types);
  }, [contracts]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleSort = (property: keyof UtilityService) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Contracts</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Contract
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search contracts..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            label="Status"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status._id} value={status.value}>
                {status.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Supplier</InputLabel>
          <Select
            value={supplierFilter}
            onChange={(e) => { setSupplierFilter(e.target.value); setPage(0); }}
            label="Supplier"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All Suppliers</MenuItem>
            {suppliers.map((supplier) => (
              <MenuItem key={supplier._id} value={supplier._id}>
                {supplier.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Service Type</InputLabel>
          <Select
            value={serviceTypeFilter}
            onChange={(e) => { setServiceTypeFilter(e.target.value); setPage(0); }}
            label="Service Type"
            startAdornment={<FilterIcon sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All Types</MenuItem>
            {uniqueServiceTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {formatServiceType(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filteredContracts.length === 0 && !error && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || serviceTypeFilter !== 'all' 
              ? 'No contracts match your search criteria' 
              : 'No contracts found'
            }
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || serviceTypeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Click the "Add Contract" button above to add your first contract.'
            }
          </Typography>
        </Paper>
      )}

      {filteredContracts.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'client'}
                      direction={orderBy === 'client' ? order : 'asc'}
                      onClick={() => handleSort('client')}
                    >
                      <Typography fontWeight="bold">Client</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'property'}
                      direction={orderBy === 'property' ? order : 'asc'}
                      onClick={() => handleSort('property')}
                    >
                      <Typography fontWeight="bold">Property</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'serviceType'}
                      direction={orderBy === 'serviceType' ? order : 'asc'}
                      onClick={() => handleSort('accountNumber')}
                    >
                      <Typography fontWeight="bold">Contract Type</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'supplier'}
                      direction={orderBy === 'supplier' ? order : 'asc'}
                      onClick={() => handleSort('supplier')}
                    >
                      <Typography fontWeight="bold">Supplier</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><Typography fontWeight="bold">Account Number</Typography></TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      <Typography fontWeight="bold">Status</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><Typography fontWeight="bold">Commission Status</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">Contract Start</Typography></TableCell>
                  <TableCell><Typography fontWeight="bold">Contract End</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedContracts.map((contract) => (
                  <StyledTableRow 
                    key={contract._id}
                    onClick={() => handleContractClick(contract)}
                  >
                    <TableCell>{contract.client?.companyName || 'N/A'}</TableCell>
                    <TableCell>{contract.property?.address?.line1 || 'N/A'}</TableCell>
                    <TableCell>{formatServiceType(contract.serviceType)}</TableCell>
                    <TableCell>{getSupplierName(contract.supplier) || 'N/A'}</TableCell>
                    <TableCell>{contract.accountNumber || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={contract.status}
                        color={getStatusColor(contract.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {contract.commissionStatus && contract.commissionStatus !== 'N/A' ? (
                        <Chip
                          label={contract.commissionStatus}
                          color={
                            contract.commissionStatus === 'Paid' ? 'success' : 
                            contract.commissionStatus === 'Partial' ? 'warning' : 
                            contract.commissionStatus === 'Overpaid' ? 'info' :
                            'default'
                          }
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {contract.contractStartDate
                        ? new Date(contract.contractStartDate).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {contract.contractEndDate
                        ? new Date(contract.contractEndDate).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={filteredContracts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </>
      )}
    </Box>
  );
};

export default Contracts;
